<template>
  <div class="flex flex-col items-center px-6 pb-6 text-lg md:px-24 md:py-12">
    <h1
      id="confgrt_final_msg"
      class="text-2xl font-bold text-center font-montserrat md:text-5.5xl md:uppercase text-indigo-base"
    >
      Click "SAVE & CONTINUE" to keep your preferences
    </h1>
    <h2 class="mt-4 md:mt-6 font-montserrat md:text-xl mb-6 max-w-[1010px] text-center text-indigo-base">
      Great job on personalizing your experience!
    </h2>
    <div class="p-2">
      <div class="w-full md:flex md:justify-between">
        <ButtonWithSpinner
          id="continue-button"
          ref="continueButton"
          data-testid="save-and-continue-button"
          type="button"
          variant="secondary"
          :grey-disabled-class="true"
          :custom-class="'w-full p-5 border-transparent uppercase font-bold bg-teal-900 text-white'"
          @click="continueApplication()"
        >
          <div class="flex gap-2 justify-center items-center">
            <span>Save & Continue</span>
            <svg width="9" height="14" viewBox="0 0 9 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M5.38866 7.00874L-9.95546e-08 12.2882L1.89181 14L9 7.00874L1.89181 -7.77409e-07L-7.14641e-07 1.71183L5.38866 7.00874Z"
                fill="currentColor"
              />
            </svg>
          </div>
        </ButtonWithSpinner>
      </div>
      <p v-if="hasError" class="error text-error-900">
        {{ hasError }}
      </p>
    </div>
  </div>
</template>

<script setup>
import { computed, inject, ref, toValue } from "vue";
import { useRouter } from "vue-router";
import { useToast } from "vue-toast-notification";
import { useStore } from "vuex";
import ButtonWithSpinner from "@/components/forms/SharedComponents/ButtonWithSpinner.vue";
import { canRegisterForPrograms } from "@/composables/authorization";
import { ERROR_TIMEOUT } from "@/constants.js";
import { trackEvent } from "@/util/eventTracker.js";

const { order } = inject("currentOrder");
const { program } = inject("program");
const { housing } = inject("housing");
const { duplicateApplication, validateApplication, application } = inject("application");
const {
  upsertApplicationOrderAndSubmission,
  stashUnsavedOrder,
  stashUnsavedHousingQuestionnaire,
  stashUnsavedProgramStepData,
} = toValue(application);
stashUnsavedOrder(order);
const store = useStore();
const router = useRouter();
const toast = useToast();
const continueButton = ref(null);
const isComponentLoading = ref(false);
const hasError = ref(null);

const APPLICATION_LOADER = "steps-completed";

application.value.registerLoadingState(APPLICATION_LOADER, isComponentLoading);

const continueApplication = async () => {
  trackEvent(
    "click",
    "Continue to Application",
    program?.programSession?.id,
    store.getters?.getCurrentUser?.participantId,
  );
  continueButton.value.startLoading();
  stashUnsavedHousingQuestionnaire(housing.questionnaire, housing.questionnaireId);
  if (store?.getters?.isLoggedIn) {
    submit();
  } else {
    stashUnsavedProgramStepData(program);
    router.push(destination.value);
  }
};

const submit = async () => {
  isComponentLoading.value = true;

  await validateApplication();
  if (duplicateApplication.value) {
    isComponentLoading.value = false;
    return;
  }

  if (!canRegisterForPrograms.value) {
    toast.open({
      message: "Registration creation is not allowed for your account type.",
      type: "error",
      position: "bottom",
      duration: ERROR_TIMEOUT,
    });
    isComponentLoading.value = false;
    return;
  }
  await upsertApplicationOrderAndSubmission();
  await router.push(destination.value);
  isComponentLoading.value = false;
};

const destination = computed(() => {
  const { applicationId, sessionId, programPageId } = toValue(application);
  const name = toValue(applicationId) ? "modify-application" : "new-application";

  const params = {
    stage: "apply",
  };

  if (toValue(applicationId)) {
    params.applicationId = toValue(applicationId);
  } else {
    params.sessionId = toValue(sessionId);
    params.programPageId = toValue(programPageId);
  }

  return {
    name,
    params,
  };
});
</script>
